import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { useFetch } from "../hooks/useFetch";
import { useFetchMedia } from "../hooks/useFetchMedia";
import { apiEndpoints, API_BASE_URL } from "../utils/api";

export const MembersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [currentPage, setCurrentPage] = useState(1);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [stats, setStats] = useState({});
  const itemsPerPage = 12;

  // Posicionar al inicio sin animación al cargar el componente
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Leer parámetros de URL
  useEffect(() => {
    const tab = searchParams.get("tipo") || "all";
    const search = searchParams.get("buscar") || "";
    const sort = searchParams.get("orden") || "name";
    const page = parseInt(searchParams.get("page") || "1");

    setActiveTab(tab);
    setSearchTerm(search);
    setSortBy(sort);
    setCurrentPage(page);
  }, [searchParams]);

  // Cargar tipos de medios desde la API
  const { data: filtersData } = useFetchMedia(apiEndpoints.mediaOutlets.filters());

  useEffect(() => {
    if (filtersData?.data?.media_types) {
      setMediaTypes(filtersData.data.media_types);
    }
  }, [filtersData]);

  // Función para obtener media_type_id desde el tab seleccionado
  const getMediaTypeId = (tabId) => {
    if (tabId === 'all') return null;
    
    // Si el tabId empieza con 'type_', extraer el ID
    if (tabId.startsWith('type_')) {
      return parseInt(tabId.replace('type_', ''));
    }
    
    // Fallback: buscar por coincidencia en el nombre para compatibilidad
    if (!mediaTypes.length) return null;
    
    const typeMap = {};
    mediaTypes.forEach(type => {
      const name = type.name.toLowerCase();
      if (name.includes('radio')) typeMap['radio'] = type.id;
      else if (name.includes('impreso') || name.includes('periódico')) typeMap['print'] = type.id;
      else if (name.includes('digital') || name.includes('web')) typeMap['digital'] = type.id;
      else if (name.includes('televisión') || name.includes('tv')) typeMap['tv'] = type.id;
      else if (name.includes('social') || name.includes('red')) typeMap['social'] = type.id;
    });
    
    return typeMap[tabId] || null;
  };

  // Construir URL de la API - usar siempre 'list' para datos completos
  const buildApiUrl = () => {
    // Siempre usar endpoint 'list' para obtener datos completos con perfiles sociales
    return apiEndpoints.mediaOutlets.list(
      currentPage,
      activeTab === 'all' && !searchTerm ? 9 : itemsPerPage, // Reducido de 100 a 50 para mejor rendimiento
      searchTerm || null,
      activeTab !== 'all' ? getMediaTypeId(activeTab) : null,
      null // city
    );
  };

  // Cargar conteo total solo una vez para la pestaña "Todos"
  const { data: totalCountData } = useFetchMedia(
    activeTab === 'all' ? apiEndpoints.mediaOutlets.list(1, 1, null, null, null) : null
  );

  // Obtener el conteo total real de la API
  useEffect(() => {
    if (totalCountData?.pagination?.total && activeTab === 'all') {
      setStats(prev => ({
        ...prev,
        all: totalCountData.pagination.total
      }));
    }
  }, [totalCountData, activeTab]);

  // Cargar medios de comunicación
  const {
    data: mediaOutlets,
    loading: mediaLoading,
    error: mediaError,
  } = useFetchMedia(buildApiUrl());

  // Calcular estadísticas solo para la pestaña activa
  useEffect(() => {
    if (mediaOutlets?.data && mediaTypes.length > 0) {
      const currentStats = { ...stats };
      
      if (activeTab === 'all') {
        // Para "todos", usar el total de la paginación o contar los datos
        currentStats.all = mediaOutlets.pagination?.total || mediaOutlets.data.length;
      } else {
        // Para pestañas específicas, contar los datos actuales
        const currentCount = mediaOutlets.data.length;
        currentStats[activeTab] = currentCount;
        
        // Si no tenemos el conteo total, usar el de la paginación
        if (!currentStats.all && mediaOutlets.pagination?.total) {
          currentStats.all = mediaOutlets.pagination.total;
        }
      }
      
      setStats(currentStats);
    }
  }, [mediaOutlets, activeTab, mediaTypes]);

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Miembros", url: "/miembros" },
  ];

  // Construir las opciones de tabs dinámicamente basándose en los tipos de medios de la API
  const getTabOptions = () => {
    const baseOptions = [
      { id: "all", label: "Todos", icon: "fas fa-th-large" }
    ];
    
    if (!mediaTypes.length) {
      // Mientras cargan los datos, mostrar opciones básicas
      return [
        ...baseOptions,
        { id: "loading", label: "Cargando...", icon: "fas fa-spinner", disabled: true }
      ];
    }

    // Generar opciones dinámicamente desde los tipos de medios de la API
    const dynamicOptions = mediaTypes.map(type => {
      const name = type.name.toLowerCase();
      
      // Determinar el ícono basándose en el nombre del tipo
      let icon = "fas fa-building"; // ícono por defecto
      let label = type.name;
      let tabId = `type_${type.id}`; // ID único basado en el ID del tipo
      
      if (name.includes('radio')) {
        icon = "fas fa-radio";
      } else if (name.includes('impreso') || name.includes('periódico') || name.includes('prensa')) {
        icon = "fas fa-newspaper";
      } else if (name.includes('digital') || name.includes('web') || name.includes('online')) {
        icon = "fas fa-laptop";
      } else if (name.includes('social') || name.includes('red')) {
        icon = "fas fa-share-alt";
      } else if (name.includes('televisión') || name.includes('tv') || name.includes('television')) {
        icon = "fas fa-tv";
      } else if (name.includes('podcast')) {
        icon = "fas fa-podcast";
      } else if (name.includes('youtube') || name.includes('video')) {
        icon = "fas fa-video";
      }
      
      return {
        id: tabId,
        label: label,
        icon: icon,
        mediaTypeId: type.id,
        mediaTypeName: type.name
      };
    });

    return [...baseOptions, ...dynamicOptions];
  };

  const tabOptions = getTabOptions();

  const sortOptions = [
    { value: "name", label: "Nombre A-Z" },
    { value: "name_desc", label: "Nombre Z-A" },
  ];

  // Actualizar URL con parámetros
  const updateURLParams = (newTab, newSearch, newSort, newPage) => {
    const params = new URLSearchParams();

    if (newTab !== "all") params.set("tipo", newTab);
    if (newSearch) params.set("buscar", newSearch);
    if (newSort !== "name") params.set("orden", newSort);
    if (newPage > 1) params.set("page", newPage.toString());

    setSearchParams(params);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    updateURLParams(tabId, searchTerm, sortBy, 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    updateURLParams(activeTab, searchTerm, sortBy, 1);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
    updateURLParams(activeTab, searchTerm, newSort, 1);
  };

  // Función para ordenar los outlets
  const sortOutlets = (outlets, sortBy) => {
    return [...outlets].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
  };

  // Procesar datos 
  const processOutletData = (rawData) => {
    if (!rawData) return [];
    
    const outletsArray = rawData.data || [];
    
    // Aplicar ordenamiento
    return sortOutlets(outletsArray, sortBy);
  };

  const outlets = processOutletData(mediaOutlets);
  const pagination = mediaOutlets?.pagination || { 
    page: currentPage, 
    pages: Math.ceil(outlets.length / itemsPerPage),
    total: outlets.length 
  };

  return (
    <>
      <SEOHead
        title="Miembros de Red Arauca"
        description="Conoce los medios de comunicación que forman parte de Red Arauca: emisoras de radio, medios impresos, digitales y creadores de contenido."
        keywords="medios comunicación, radio, prensa, digital, arauca, red medios, periodismo"
      />

      <div className="members-page">
        {/* Header con imagen de fondo */}
        <header className="page-header">
          <div className="header-container-left">
            <div className="header-content-left">
              <h1 className="display-4 fw-bold mb-0 mt-4">
                Nuestros Miembros
              </h1>
              
              {/* Breadcrumbs dentro del header */}
              <div className="breadcrumbs-section-header">
                <Breadcrumbs items={breadcrumbItems} />
              </div>

              {/* Stats de la página */}
              <div className="header-stats">
                <span className="stat-badge">
                  <i className="fas fa-building"></i>
                  {stats.all || 0}+ Medios Afiliados
                </span>
                <span className="stat-badge">
                  <i className="fas fa-users"></i>
                  500K+ Lectores
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="container">
          {/* Controles de búsqueda y filtros */}
          <div className="members-controls">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-group">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar medios..."
                  className="search-input"
                />
                <button type="submit" className="search-button-media">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>

            <div className="sort-controls">
              <label htmlFor="sort-select">Ordenar por:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="sort-select"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tabs de categorías */}
          <div className="members-tabs">
            {tabOptions.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && handleTabChange(tab.id)}
                className={`tab-button ${activeTab === tab.id ? "active" : ""} ${tab.disabled ? "disabled" : ""}`}
                disabled={tab.disabled}
              >
                <i className={tab.icon}></i>
                <span>{tab.label}</span>
                {stats && stats[tab.id] !== undefined && !tab.disabled && (
                  <span className="tab-count">({stats[tab.id]})</span>
                )}
              </button>
            ))}
          </div>

          {/* Contenido de miembros */}
          <section className="members-content">
            {mediaLoading ? (
              <LoadingSkeleton />
            ) : outlets.length > 0 ? (
              <>
                <div className="members-grid">
                  {outlets.map((outlet) => (
                    <MediaOutletCard key={outlet.id} outlet={outlet} />
                  ))}
                </div>

                {/* Paginación */}
                {pagination.pages > 1 && (
                  <div className="pagination-container">
                    <button
                      onClick={() => {
                        const newPage = currentPage - 1;
                        setCurrentPage(newPage);
                        updateURLParams(activeTab, searchTerm, sortBy, newPage);
                      }}
                      disabled={currentPage === 1}
                      className="pagination-button"
                    >
                      <i className="fas fa-chevron-left"></i>
                      Anterior
                    </button>

                    <span className="pagination-info">
                      Página {currentPage} de {pagination.pages}
                    </span>

                    <button
                      onClick={() => {
                        const newPage = currentPage + 1;
                        setCurrentPage(newPage);
                        updateURLParams(activeTab, searchTerm, sortBy, newPage);
                      }}
                      disabled={currentPage === pagination.pages}
                      className="pagination-button"
                    >
                      Siguiente
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            ) : mediaError ? (
              <ErrorState />
            ) : (
              <EmptyState searchTerm={searchTerm} activeTab={activeTab} />
            )}
          </section>
        </div>

        <style jsx>{`
          .members-page {
            background: #f8fafc;
            min-height: 100vh;
            padding: 0 0 60px;
          }

          .page-header {
            background: linear-gradient(
                to left,
                transparent 50%,
                rgba(0, 0, 0, 0.6) 100%
              ),
              url("https://img.freepik.com/premium-photo/3d-illustration-background-advertising-wallpaper-retro-art-90-scene-3d-rendering-decorative-concept_83608-37.jpg");
            background-size: cover;
            background-position: center;
            color: white;
            height: 220px;
            position: relative;
            display: flex;
            align-items: center;
            margin: 0;
          }

          .page-header::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
          }

          .page-header .header-container-left {
            position: relative;
            z-index: 2;
            width: 100%;
            padding-left: 40px;
            padding-right: 20px;
          }

          .header-content-left {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            height: 100%;
            text-align: left;
            max-width: 600px;
          }

          .page-header h1 {
            font-size: 2.5rem;
            font-weight: 800;
            margin: 0;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
            opacity: 0.95;
            line-height: 1.2;
          }

          .breadcrumbs-section-header {
            margin: 16px 0 8px 0;
          }

          .breadcrumbs-section-header :global(nav) {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }

          .breadcrumbs-section-header :global(.breadcrumb-container) {
            padding: 0 !important;
          }

          .breadcrumbs-section-header :global(.breadcrumb-link) {
            color: rgba(255, 255, 255, 0.9) !important;
            text-decoration: underline;
          }

          .breadcrumbs-section-header :global(.breadcrumb-link:hover) {
            color: white !important;
          }

          .breadcrumbs-section-header :global(.breadcrumb-current) {
            color: rgba(255, 255, 255, 0.8) !important;
            font-weight: 600;
          }

          .breadcrumbs-section-header :global(.breadcrumb-separator) {
            color: rgba(255, 255, 255, 0.6) !important;
          }

          .header-stats {
            display: flex;
            gap: 16px;
            margin-top: 12px;
            flex-wrap: wrap;
          }

          .stat-badge {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.9rem;
            font-weight: 600;
            padding: 6px 12px;
            border-radius: 20px;
            background: rgba(255, 255, 255, 0.2);
            backdrop-filter: blur(5px);
            border: 1px solid rgba(255, 255, 255, 0.3);
            color: rgba(255, 255, 255, 0.95);
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px 20px 0;
          }

          .members-controls {
            background: white;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 20px;
            flex-wrap: wrap;
          }

          .search-form {
            flex: 1;
            max-width: 400px;
          }

          .search-input-group {
            display: flex;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
            transition: border-color 0.2s ease;
          }

          .search-input-group:focus-within {
            border-color: #3b82f6;
          }

          .search-input {
            flex: 1;
            padding: 12px 16px;
            border: none;
            background: transparent;
            font-size: 1rem;
            color: #374151;
          }

          .search-input:focus {
            outline: none;
          }

          .search-button-media {
            padding: 12px 16px;
            background: #3b82f6;
            color: white;
            border: none;
            cursor: pointer;
            transition: background 0.2s ease;
          }

          .search-button-media:hover {
            background: #2563eb;
          }

          .sort-controls {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .sort-controls label {
            font-weight: 500;
            color: #374151;
            white-space: nowrap;
          }

          .sort-select {
            padding: 8px 12px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            background: white;
            color: #374151;
            font-size: 0.9rem;
            min-width: 150px;
          }

          .sort-select:focus {
            outline: none;
            border-color: #3b82f6;
          }

          .members-tabs {
            display: flex;
            gap: 8px;
            margin-bottom: 30px;
            overflow-x: auto;
            padding: 4px;
          }

          .tab-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            color: #6b7280;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .tab-button:hover {
            border-color: #3b82f6;
            color: #3b82f6;
          }

          .tab-button.active {
            background: #3b82f6;
            border-color: #3b82f6;
            color: white;
          }

          .tab-button.disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
          }

          .tab-button.disabled .fas.fa-spinner {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .tab-count {
            font-size: 0.8rem;
            opacity: 0.8;
          }

          .members-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
            gap: 24px;
            margin-bottom: 40px;
          }

          .pagination-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 20px;
            padding: 20px;
            background: white;
            border-radius: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }

          .pagination-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s ease;
          }

          .pagination-button:hover:not(:disabled) {
            background: #2563eb;
          }

          .pagination-button:disabled {
            background: #d1d5db;
            cursor: not-allowed;
          }

          .pagination-info {
            color: #6b7280;
            font-weight: 500;
          }

          @media (max-width: 768px) {
            .page-header {
              height: 180px;
            }

            .page-header .header-container-left {
              padding-left: 20px;
              padding-right: 16px;
            }

            .page-header h1 {
              font-size: 1.8rem;
            }

            .header-stats {
              gap: 8px;
            }

            .stat-badge {
              font-size: 0.8rem;
              padding: 4px 8px;
            }

            .container {
              padding: 24px 16px 0;
            }

            .members-controls {
              flex-direction: column;
              align-items: stretch;
            }

            .search-form {
              max-width: none;
            }

            .sort-controls {
              justify-content: center;
            }

            .members-grid {
              grid-template-columns: 1fr;
            }

            .breadcrumbs-section-header :global(.breadcrumb-list) {
              font-size: 0.8rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

// Componente para cada medio de comunicación
const MediaOutletCard = ({ outlet }) => {
  const getTypeIcon = (mediaType) => {
    const typeName = (typeof mediaType === 'string' ? mediaType : mediaType?.name)?.toLowerCase() || '';
    
    if (typeName.includes('radio')) return "fas fa-radio";
    if (typeName.includes('impreso') || typeName.includes('periodico')) return "fas fa-newspaper";
    if (typeName.includes('digital') || typeName.includes('web')) return "fas fa-laptop";
    if (typeName.includes('television') || typeName.includes('television')) return "fas fa-tv";
    if (typeName.includes('social') || typeName.includes('red')) return "fas fa-share-alt";
    
    return "fas fa-building";
  };

  const getTypeColor = (mediaType) => {
    const typeName = (typeof mediaType === 'string' ? mediaType : mediaType?.name)?.toLowerCase() || '';
    
    if (typeName.includes('radio')) return "#3b82f6";
    if (typeName.includes('impreso') || typeName.includes('periodico')) return "#10b981";
    if (typeName.includes('digital') || typeName.includes('web')) return "#f59e0b";
    if (typeName.includes('television') || typeName.includes('television')) return "#ef4444";
    if (typeName.includes('social') || typeName.includes('red')) return "#8b5cf6";
    
    return "#6b7280";
  };

  const logoUrl = outlet.logo;
  const mediaTypeName = typeof outlet.media_type === 'string' ? outlet.media_type : outlet.media_type?.name;
  const cityName = typeof outlet.city === 'string' ? outlet.city : outlet.city?.name;
  const userName = typeof outlet.user === 'string' ? outlet.user : outlet.user?.name;

  return (
    <div className="outlet-card">
      <div className="outlet-header">
        <div className="outlet-logo">
          {logoUrl ? (
            <img 
              src={logoUrl.startsWith('http') ? logoUrl : `${API_BASE_URL}${logoUrl}`} 
              alt={outlet.name} 
            />
          ) : (
            <div className="logo-placeholder">
              <i className={getTypeIcon(outlet.media_type)}></i>
            </div>
          )}
        </div>
        <div
          className="outlet-type-badge"
          style={{ backgroundColor: getTypeColor(outlet.media_type) }}
        >
          <i className={getTypeIcon(outlet.media_type)}></i>
          {mediaTypeName || 'Medio'}
        </div>
      </div>

      <div className="outlet-content">
        <h3 className="outlet-name">{outlet.name}</h3>
        <p className="outlet-description">{outlet.description}</p>

        <div className="outlet-meta">
          {cityName && (
            <span className="meta-item">
              <i className="fas fa-map-marker-alt"></i>
              {cityName}
            </span>
          )}
          {outlet.coverage_area && (
            <span className="meta-item">
              <i className="fas fa-globe"></i>
              {outlet.coverage_area}
            </span>
          )}
          {userName && (
            <span className="meta-item">
              <i className="fas fa-user"></i>
              {userName}
            </span>
          )}
          {outlet.created_at && (
            <span className="meta-item">
              <i className="fas fa-calendar"></i>
              Miembro desde {new Date(outlet.created_at).getFullYear()}
            </span>
          )}
        </div>
      </div>

      <div className="outlet-actions">
        {outlet.website_url && (
          <a
            href={outlet.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="action-button primary"
          >
            <i className="fas fa-globe"></i>
            Sitio Web
          </a>
        )}

        <div className="social-links-media">
          {outlet.social_profiles && outlet.social_profiles.length > 0 && outlet.social_profiles.map((profile, index) => {
            const platform = profile.platform?.toLowerCase();
            let iconClass = 'fab fa-globe';
            
            if (platform?.includes('facebook')) iconClass = 'fab fa-facebook-f';
            else if (platform?.includes('twitter') || platform?.includes('x')) iconClass = 'fab fa-twitter';
            else if (platform?.includes('instagram')) iconClass = 'fab fa-instagram';
            else if (platform?.includes('youtube')) iconClass = 'fab fa-youtube';
            else if (platform?.includes('linkedin')) iconClass = 'fab fa-linkedin-in';
            else if (platform?.includes('tiktok')) iconClass = 'fab fa-tiktok';

            return (
              <a
                key={index}
                href={profile.url}
                target="_blank"
                rel="noopener noreferrer"
                title={`${profile.platform}${profile.verified ? ' (Verificado)' : ''}`}
              >
                <i className={iconClass}></i>
                {profile.verified && <i className="fas fa-check-circle verified-badge"></i>}
              </a>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .outlet-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .outlet-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .outlet-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .outlet-logo {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
        }

        .outlet-logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .logo-placeholder {
          width: 100%;
          height: 100%;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 1.5rem;
        }

        .outlet-type-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 20px;
          color: white;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .outlet-content {
          flex: 1;
          margin-bottom: 20px;
        }

        .outlet-name {
          font-size: 1.25rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 8px;
          line-height: 1.3;
        }

        .outlet-description {
          color: #6b7280;
          font-size: 0.9rem;
          line-height: 1.5;
          margin-bottom: 16px;
        }

        .outlet-meta {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.85rem;
          color: #6b7280;
        }

        .meta-item i {
          width: 14px;
          color: #9ca3af;
        }

        .outlet-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 16px;
          border-top: 1px solid #f3f4f6;
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .action-button.primary {
          background: #3b82f6;
          color: white;
        }

        .action-button.primary:hover {
          background: #2563eb;
          color: white;
        }

        .social-links-media {
          display: flex;
          gap: 8px;
        }

        .social-links-media a {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          border: 1px solid #e5e7eb;
          transition: all 0.2s ease;
          text-decoration: none;
          position: relative;
        }

        .social-links-media a:hover {
          color: #3b82f6;
          border-color: #3b82f6;
        }

        .verified-badge {
          position: absolute;
          top: -2px;
          right: -2px;
          color: #10b981;
          font-size: 0.6rem;
          background: white;
          border-radius: 50%;
        }
      `}</style>
    </div>
  );
};

// Componentes auxiliares
const LoadingSkeleton = () => (
  <div className="members-grid">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <div
        key={i}
        className="loading-skeleton"
        style={{ height: "300px", borderRadius: "12px" }}
      ></div>
    ))}
  </div>
);

const ErrorState = () => (
  <div className="error-state">
    <div className="error-icon">
      <i className="fas fa-exclamation-triangle"></i>
    </div>
    <h3>Error al cargar los miembros</h3>
    <p>Hubo un problema al cargar la información de nuestros miembros.</p>
    <button onClick={() => window.location.reload()} className="retry-button">
      <i className="fas fa-redo"></i>
      Intentar de nuevo
    </button>
    <style jsx>{`
      .error-state {
        text-align: center;
        padding: 60px 20px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }
      .error-icon {
        font-size: 4rem;
        color: #f59e0b;
        margin-bottom: 20px;
      }
      .retry-button {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        margin-top: 16px;
      }
      .retry-button:hover {
        background: #2563eb;
      }
    `}</style>
  </div>
);

const EmptyState = ({ searchTerm, activeTab }) => (
  <div className="empty-state">
    <div className="empty-icon">
      <i className="fas fa-search"></i>
    </div>
    <h3>
      {searchTerm
        ? `No se encontraron resultados para "${searchTerm}"`
        : `No hay miembros en esta categoría`}
    </h3>
    <p>
      {searchTerm
        ? "Intenta con términos diferentes o revisa la ortografía."
        : `Aún no hay medios registrados en la categoría ${activeTab}.`}
    </p>
    <style jsx>{`
      .empty-state {
        text-align: center;
        padding: 60px 20px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }
      .empty-icon {
        font-size: 4rem;
        color: #d1d5db;
        margin-bottom: 20px;
      }
      .empty-state h3 {
        color: #374151;
        margin-bottom: 12px;
      }
      .empty-state p {
        color: #6b7280;
      }
    `}</style>
  </div>
);